/*
  <sm-select> — a dropdown the theme draws itself.

  A native <select> renders its list with the operating system, so it arrives
  in the system font at the system size with the system highlight colour, and
  nothing about it can be styled. This wraps one and draws a list in the
  theme's own type instead.

  The real <select> stays in the markup and stays the source of truth. Picking
  an option sets its value and fires the events it would have fired itself, so
  the filter form behaves exactly as before: same name, same value, same
  submit. With no script the select is simply visible and works on its own.

  Because Shopify re-renders the filter bar over AJAX, this has to survive
  being thrown away and rebuilt. It does, because a custom element upgrades
  itself whenever its markup is inserted.
*/
if (!customElements.get('sm-select')) {
  customElements.define(
    'sm-select',
    class SmSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        if (!this.select || this.built) return;
        this.built = true;
        this.setAttribute('data-ready', '');

        // Dawn's own caret belongs to the native control, which is now hidden.
        this.querySelectorAll(':scope > .svg-wrapper').forEach((el) => el.remove());

        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'sm-select__toggle';
        this.toggle.setAttribute('aria-haspopup', 'listbox');
        this.toggle.setAttribute('aria-expanded', 'false');

        /* The native select carries the accessible name, usually from a label
           pointing at its id. Borrow it rather than inventing one. */
        const label = this.select.labels && this.select.labels[0];
        if (label) this.toggle.setAttribute('aria-label', label.textContent.trim());

        this.value = document.createElement('span');
        this.value.className = 'sm-select__value';
        this.toggle.appendChild(this.value);

        const caret = document.createElement('span');
        caret.className = 'sm-select__caret';
        caret.setAttribute('aria-hidden', 'true');
        this.toggle.appendChild(caret);

        this.menu = document.createElement('ul');
        this.menu.className = 'sm-select__menu';
        this.menu.setAttribute('role', 'listbox');
        this.menu.hidden = true;

        Array.from(this.select.options).forEach((option) => {
          const li = document.createElement('li');
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'sm-select__option';
          item.setAttribute('role', 'option');
          item.dataset.value = option.value;
          item.textContent = option.textContent.trim();
          item.setAttribute('aria-selected', String(option.selected));
          item.addEventListener('click', () => this.pick(option.value));
          li.appendChild(item);
          this.menu.appendChild(li);
        });

        this.appendChild(this.toggle);
        this.appendChild(this.menu);
        this.sync();

        this.toggle.addEventListener('click', () => this.setOpen(this.menu.hidden));
        this.addEventListener('keydown', this.onKeyDown.bind(this));

        /* Anything outside closes it. Listening on the document means a click
           on another dropdown closes this one on the way past. */
        this.onOutside = (e) => {
          if (!this.contains(e.target)) this.setOpen(false);
        };

        // Something else may change the select: a reset, or the back button.
        this.select.addEventListener('change', () => this.sync());
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onOutside);
      }

      sync() {
        const option = this.select.options[this.select.selectedIndex];
        this.value.textContent = option ? option.textContent.trim() : '';
        this.menu.querySelectorAll('.sm-select__option').forEach((item) => {
          item.setAttribute('aria-selected', String(item.dataset.value === this.select.value));
        });
      }

      setOpen(open) {
        this.menu.hidden = !open;
        this.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', String(open));

        if (open) {
          document.addEventListener('pointerdown', this.onOutside);
          const current = this.menu.querySelector('[aria-selected="true"]') || this.menu.querySelector('.sm-select__option');
          if (current) current.focus();
        } else {
          document.removeEventListener('pointerdown', this.onOutside);
        }
      }

      pick(value) {
        this.setOpen(false);
        this.toggle.focus();
        if (value === this.select.value) return;

        this.select.value = value;
        this.sync();

        /* The filter form listens for input, and other code may listen for
           change, so send both the way the select would have. */
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onKeyDown(event) {
        const items = Array.from(this.menu.querySelectorAll('.sm-select__option'));
        const here = items.indexOf(document.activeElement);

        switch (event.key) {
          case 'Escape':
            if (this.menu.hidden) return;
            this.setOpen(false);
            this.toggle.focus();
            break;
          case 'ArrowDown':
          case 'ArrowUp': {
            if (this.menu.hidden) {
              this.setOpen(true);
              break;
            }
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = items[(here + step + items.length) % items.length];
            if (next) next.focus();
            break;
          }
          case 'Home':
            if (!this.menu.hidden && items[0]) items[0].focus();
            break;
          case 'End':
            if (!this.menu.hidden && items.length) items[items.length - 1].focus();
            break;
          default:
            return;
        }

        event.preventDefault();
      }
    }
  );
}
